import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../auth';
import { sipManager, type SipSessionEvent } from './SipManager';
import { telephonyApi } from './telephonyApi';
import { SIP_CONFIG_REFRESH_INTERVAL_MS } from './SipCredentialRegeneration';
import { MediaQualityReporter } from './MediaQualityReporter';
import {
  IdentityGenerationGuard,
  type IdentityGenerationToken,
} from './IdentityGenerationGuard';
import {
  normalizeTransferDestination,
  transferProgressMessage,
} from './TransferTargets';
import type {
  BrowserCall,
  BrowserPhoneConfig,
  CallResultPayload,
  OperatorStatus,
  OutboundCallPayload,
  SipSnapshot,
  TelephonyEvent,
} from './types';

type SipPhoneContextValue = {
  config: BrowserPhoneConfig | null;
  configLoading: boolean;
  snapshot: SipSnapshot;
  call: BrowserCall | null;
  operatorStatus: OperatorStatus;
  actionPending: boolean;
  statusMessage: string;
  refreshConfig: () => Promise<unknown>;
  requestMicrophone: () => Promise<void>;
  activateThisTab: () => Promise<void>;
  dial: (payload: OutboundCallPayload) => Promise<void>;
  answer: () => Promise<void>;
  decline: () => Promise<void>;
  hangup: () => Promise<void>;
  toggleMute: () => Promise<void>;
  toggleHold: () => Promise<void>;
  sendDtmf: (tone: string) => void;
  transfer: (destination: string) => Promise<void>;
  selectTicket: (ticketId: string) => Promise<void>;
  saveResult: (payload: CallResultPayload) => Promise<void>;
  setOperatorStatus: (status: OperatorStatus) => Promise<void>;
  dismissPostProcessing: () => void;
};

const SipPhoneContext = createContext<SipPhoneContextValue | null>(null);

const INITIAL_SNAPSHOT: SipSnapshot = {
  state: 'OFFLINE',
  connected: false,
  registered: false,
  muted: false,
  held: false,
  primaryTab: false,
  sessionDirection: null,
  remoteNumber: '',
  error: '',
  startedAt: null,
  endedAt: null,
};

function callNumber(call: BrowserCall | null): string {
  if (!call) return '';
  return call.direction === 'INBOUND'
    ? String(call.from_number || call.to_number || '')
    : String(call.to_number || call.from_number || '');
}

function dispositionForFailed(event: SipSessionEvent): 'MISSED' | 'CANCELLED' | 'FAILED' {
  if (event.answeredElsewhere) return 'CANCELLED';
  return event.direction === 'INBOUND' ? 'MISSED' : 'FAILED';
}

function restoreServerCallClock(call: BrowserCall | null | undefined) {
  sipManager.restoreCallStartedAt(call?.answered_at || null);
}

export function SipPhoneProvider({ enabled, children }: { enabled: boolean; children: ReactNode }) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const mountedRef = useRef(false);
  const enabledRef = useRef(enabled);
  const identityGenerationRef = useRef<IdentityGenerationGuard | null>(null);
  if (!identityGenerationRef.current) {
    identityGenerationRef.current = new IdentityGenerationGuard(user?.id ?? null);
  } else {
    identityGenerationRef.current.switchIdentity(user?.id ?? null);
  }
  if (enabledRef.current !== enabled) {
    enabledRef.current = enabled;
    identityGenerationRef.current.invalidate();
  }
  const [snapshot, setSnapshot] = useState<SipSnapshot>(INITIAL_SNAPSHOT);
  const [call, setCall] = useState<BrowserCall | null>(null);
  const [operatorStatus, setOperatorStatusState] = useState<OperatorStatus>('OFFLINE');
  const [actionPending, setActionPending] = useState(false);
  const [statusMessage, setStatusMessage] = useState('');
  const activeCallIdRef = useRef<string | null>(null);
  const sessionRegistrationRef = useRef<Promise<BrowserCall | null> | null>(null);
  const finalizedRef = useRef<Set<string>>(new Set());
  const answeredRef = useRef(false);
  const pageExitingRef = useRef(false);
  const intentionalEndRef = useRef(false);
  const transferInProgressRef = useRef<{ callId: string; destination: string } | null>(null);
  const recoveryInFlightRef = useRef(false);
  const lastRecoveryAttemptRef = useRef(0);
  const qualityReporterRef = useRef<MediaQualityReporter | null>(null);
  if (!qualityReporterRef.current) {
    qualityReporterRef.current = new MediaQualityReporter({
      send: (callId, quality) => telephonyApi.reportQuality(callId, quality),
      onError: error => console.warn('[telephony] quality report failed', error),
    });
  }

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      identityGenerationRef.current?.invalidate();
    };
  }, []);

  const captureIdentityGeneration = useCallback(
    (): IdentityGenerationToken => identityGenerationRef.current!.capture(),
    [],
  );
  const isIdentityGenerationCurrent = useCallback(
    (generation: IdentityGenerationToken): boolean => Boolean(
      mountedRef.current
      && enabledRef.current
      && identityGenerationRef.current?.isCurrent(generation)
    ),
    [],
  );

  const configQuery = useQuery({
    queryKey: ['telephony-browser-config', user?.id],
    queryFn: telephonyApi.config,
    enabled: enabled && user?.userType === 'EMPLOYEE',
    staleTime: SIP_CONFIG_REFRESH_INTERVAL_MS,
    retry: 2,
  });
  const config = configQuery.data || null;

  const sipConnectionKey = JSON.stringify({
    enabled,
    configured: config?.configured || false,
    mode: config?.mode || '',
    webSocketUrl: config?.webSocketUrl || '',
    sipDomain: config?.sipDomain || '',
    outboundProxy: config?.outboundProxy || '',
    registrationExpires: config?.registrationExpires || 0,
    iceServers: config?.iceServers || [],
    account: config?.account ? {
      id: config.account.id,
      extension: config.account.extension,
      outboundNumber: config.account.outboundNumber,
      displayName: config.account.displayName,
      sipUri: config.account.sipUri,
      authUser: config.account.authUser,
      password: config.account.password,
      allowInbound: config.account.allowInbound,
      allowOutbound: config.account.allowOutbound,
      allowTransfer: config.account.allowTransfer,
      allowRecordings: config.account.allowRecordings,
    } : null,
  });

  const updateOperatorStatus = useCallback(async (status: OperatorStatus) => {
    const generation = captureIdentityGeneration();
    if (!isIdentityGenerationCurrent(generation)) return;
    setOperatorStatusState(status);
    try {
      await telephonyApi.operatorStatus(status);
      if (!isIdentityGenerationCurrent(generation)) return;
      queryClient.setQueryData(['telephony-browser-config', user?.id], (current: BrowserPhoneConfig | undefined) => current?.account
        ? { ...current, account: { ...current.account, operatorStatus: status } }
        : current);
    } catch (error) {
      if (!isIdentityGenerationCurrent(generation)) return;
      setStatusMessage(error instanceof Error ? error.message : 'Не удалось изменить статус оператора');
      throw error;
    }
  }, [captureIdentityGeneration, isIdentityGenerationCurrent, queryClient, user?.id]);

  const activateThisTab = useCallback(async () => {
    const generation = captureIdentityGeneration();
    if (!isIdentityGenerationCurrent(generation)) return;
    setActionPending(true);
    setStatusMessage('');
    try {
      await sipManager.activateThisTab();
      if (!isIdentityGenerationCurrent(generation)) return;
      setStatusMessage('Телефон активирован в этой вкладке');
    } catch (error) {
      if (!isIdentityGenerationCurrent(generation)) return;
      const message = error instanceof Error ? error.message : 'Не удалось активировать телефон в этой вкладке';
      setStatusMessage(message);
      throw error;
    } finally {
      if (isIdentityGenerationCurrent(generation)) setActionPending(false);
    }
  }, [captureIdentityGeneration, isIdentityGenerationCurrent]);

  const requestMicrophone = useCallback(async () => {
    const generation = captureIdentityGeneration();
    if (!isIdentityGenerationCurrent(generation)) return;
    try {
      await sipManager.requestMicrophone();
      if (!isIdentityGenerationCurrent(generation)) return;
    } catch (error) {
      if (!isIdentityGenerationCurrent(generation)) return;
      throw error;
    }
  }, [captureIdentityGeneration, isIdentityGenerationCurrent]);

  const ensureCallId = useCallback(async (generation: IdentityGenerationToken): Promise<string | null> => {
    if (!isIdentityGenerationCurrent(generation)) return null;
    if (activeCallIdRef.current) return activeCallIdRef.current;
    const registered = await sessionRegistrationRef.current;
    if (!isIdentityGenerationCurrent(generation)) return null;
    return registered?.id || activeCallIdRef.current;
  }, [isIdentityGenerationCurrent]);

  const finishCall = useCallback(async (
    disposition: 'COMPLETED' | 'MISSED' | 'CANCELLED' | 'FAILED',
    reason = '',
  ): Promise<BrowserCall | null> => {
    const generation = captureIdentityGeneration();
    const id = await ensureCallId(generation);
    if (!isIdentityGenerationCurrent(generation)) return null;
    if (!id || finalizedRef.current.has(id)) return null;
    finalizedRef.current.add(id);
    try {
      const updated = await telephonyApi.finish(id, disposition, reason);
      if (!isIdentityGenerationCurrent(generation)) return null;
      setCall(current => ({ ...(current || updated), ...updated }));
      await queryClient.invalidateQueries({ queryKey: ['telephony-calls'] });
      if (!isIdentityGenerationCurrent(generation)) return null;
      return updated;
    } catch (error) {
      if (!isIdentityGenerationCurrent(generation)) return null;
      finalizedRef.current.delete(id);
      setStatusMessage(error instanceof Error ? error.message : 'Не удалось завершить звонок в журнале');
      return null;
    }
  }, [captureIdentityGeneration, ensureCallId, isIdentityGenerationCurrent, queryClient]);

  const registerSession = useCallback((event: SipSessionEvent) => {
    const generation = captureIdentityGeneration();
    if (!isIdentityGenerationCurrent(generation)) return;
    const knownCallId = event.callId || activeCallIdRef.current;
    if (event.recovery) {
      answeredRef.current = true;
      setStatusMessage('Восстанавливаем разговор…');
    }
    const promise = telephonyApi.registerSession({
      direction: event.originalDirection || event.direction,
      phone: event.remoteNumber || callNumber(call),
      sessionId: event.sessionId,
      ...(knownCallId ? { callId: knownCallId } : {}),
      ...(event.anchorId ? { anchorId: event.anchorId } : {}),
      ...(event.externalUuid ? { externalUuid: event.externalUuid } : {}),
      ...(event.recovery ? { recovery: true } : {}),
    })
      .then(created => {
        if (!isIdentityGenerationCurrent(generation)) return null;
        activeCallIdRef.current = created.id;
        finalizedRef.current.delete(created.id);
        setCall(current => ({ ...(current || created), ...created }));
        if (event.recovery) restoreServerCallClock(created);
        else sipManager.restoreCallStartedAt(null);
        return created;
      })
      .catch(error => {
        if (!isIdentityGenerationCurrent(generation)) return null;
        setStatusMessage(error instanceof Error ? error.message : 'Не удалось зарегистрировать SIP-сессию');
        return null;
      });
    sessionRegistrationRef.current = promise;
  }, [call, captureIdentityGeneration, isIdentityGenerationCurrent]);

  const recoverActiveCall = useCallback(async () => {
    const generation = captureIdentityGeneration();
    if (!isIdentityGenerationCurrent(generation)) return null;
    const sip = sipManager.getSnapshot();
    if (!sip.registered || !sip.primaryTab || pageExitingRef.current) return null;
    if (recoveryInFlightRef.current) return null;
    if (Date.now() - lastRecoveryAttemptRef.current < 1_200) return null;

    recoveryInFlightRef.current = true;
    lastRecoveryAttemptRef.current = Date.now();
    try {
      const recovered = await telephonyApi.recoverActive();
      if (!isIdentityGenerationCurrent(generation)) return null;
      if (!recovered.call) return null;
      const active = recovered.call;
      activeCallIdRef.current = active.id;
      finalizedRef.current.delete(active.id);
      answeredRef.current = true;
      setCall(current => ({ ...(current || active), ...active }));
      restoreServerCallClock(active);
      if (recovered.recoveryStarted) setStatusMessage('Восстанавливаем разговор…');
      return recovered;
    } catch (error) {
      if (!isIdentityGenerationCurrent(generation)) return null;
      setStatusMessage(error instanceof Error ? error.message : 'Не удалось восстановить разговор');
      return null;
    } finally {
      if (isIdentityGenerationCurrent(generation)) recoveryInFlightRef.current = false;
    }
  }, [captureIdentityGeneration, isIdentityGenerationCurrent]);

  useEffect(() => sipManager.on('snapshot', next => {
    setSnapshot(next);
    if (next.error) {
      setStatusMessage(next.error);
    } else if (['READY', 'RINGING', 'DIALING', 'ACTIVE', 'HOLD'].includes(next.state)) {
      setStatusMessage('');
    }
  }), []);

  useEffect(() => sipManager.on('mediaQuality', quality => {
    void qualityReporterRef.current?.observe(activeCallIdRef.current, quality);
  }), []);

  useEffect(() => sipManager.on('registration', ({ registered, reason }) => {
    const generation = captureIdentityGeneration();
    if (!isIdentityGenerationCurrent(generation)) return;
    void telephonyApi.registration(registered).catch(() => undefined);
    if (registered) {
      pageExitingRef.current = false;
      setStatusMessage('Встроенный телефон зарегистрирован');
      if (['OFFLINE', 'POST_PROCESSING'].includes(operatorStatus)) void updateOperatorStatus('AVAILABLE').catch(() => undefined);
      window.setTimeout(() => {
        if (isIdentityGenerationCurrent(generation)) void recoverActiveCall();
      }, 350);
    } else if (reason) {
      setStatusMessage(
        reason === 'disconnected'
          ? 'Соединение с АТС восстанавливается…'
          : reason === 'unregistered'
            ? 'Телефон переподключается…'
            : reason,
      );
    }
  }), [captureIdentityGeneration, isIdentityGenerationCurrent, operatorStatus, recoverActiveCall, updateOperatorStatus]);

  useEffect(() => sipManager.on('sessionNew', event => {
    answeredRef.current = false;
    registerSession(event);
  }), [registerSession]);

  useEffect(() => sipManager.on('sessionAccepted', event => {
    const generation = captureIdentityGeneration();
    if (!isIdentityGenerationCurrent(generation)) return;
    answeredRef.current = true;
    void (async () => {
      const id = await ensureCallId(generation);
      if (!isIdentityGenerationCurrent(generation)) return;
      if (id) {
        try {
          const updated = await telephonyApi.answer(id);
          if (!isIdentityGenerationCurrent(generation)) return;
          setCall(current => ({ ...(current || updated), ...updated }));
          restoreServerCallClock(updated);
          setStatusMessage(event.recovery ? 'Разговор восстановлен' : 'Звонок принят');
        } catch (error) {
          if (!isIdentityGenerationCurrent(generation)) return;
          setStatusMessage(error instanceof Error ? error.message : 'Звонок принят, но журнал не обновлён');
        }
      }
      if (!isIdentityGenerationCurrent(generation)) return;
      await updateOperatorStatus('CALL').catch(() => undefined);
    })();
  }), [captureIdentityGeneration, ensureCallId, isIdentityGenerationCurrent, updateOperatorStatus]);

  const clearFinishedCall = useCallback(async (message = '') => {
    const generation = captureIdentityGeneration();
    if (!isIdentityGenerationCurrent(generation)) return;
    void qualityReporterRef.current?.flush();
    setCall(null);
    activeCallIdRef.current = null;
    sessionRegistrationRef.current = null;
    answeredRef.current = false;
    sipManager.restoreCallStartedAt(null);
    setStatusMessage(message);
    if (!isIdentityGenerationCurrent(generation)) return;
    await updateOperatorStatus(snapshot.registered ? 'AVAILABLE' : 'OFFLINE').catch(() => undefined);
  }, [captureIdentityGeneration, isIdentityGenerationCurrent, snapshot.registered, updateOperatorStatus]);

  useEffect(() => sipManager.on('sessionEnded', event => {
    const generation = captureIdentityGeneration();
    if (!isIdentityGenerationCurrent(generation)) return;
    void (async () => {
      if (intentionalEndRef.current || pageExitingRef.current) return;

      const transfer = transferInProgressRef.current;
      if (transfer) {
        transferInProgressRef.current = null;
        await clearFinishedCall(`Звонок переведён на добавочный ${transfer.destination}`);
        if (!isIdentityGenerationCurrent(generation)) return;
        return;
      }

      if (answeredRef.current) {
        const id = await ensureCallId(generation);
        if (!isIdentityGenerationCurrent(generation)) return;
        if (id) {
          try {
            const detached = await telephonyApi.detached(id);
            if (!isIdentityGenerationCurrent(generation)) return;
            if (detached.recoverable) {
              const detachedCall = detached.call;
              if (detachedCall) {
                setCall(current => ({ ...(current || detachedCall), ...detachedCall }));
                restoreServerCallClock(detachedCall);
              }
              setStatusMessage('Связь с браузером потеряна. Восстанавливаем разговор…');
              window.setTimeout(() => {
                if (isIdentityGenerationCurrent(generation)) void recoverActiveCall();
              }, 250);
              return;
            }
          } catch (error) {
            if (!isIdentityGenerationCurrent(generation)) return;
            setStatusMessage(error instanceof Error ? error.message : 'Не удалось проверить активный разговор');
          }
        }
      }

      await finishCall('COMPLETED', event.cause || '');
      if (!isIdentityGenerationCurrent(generation)) return;
      await clearFinishedCall('Звонок завершён');
    })();
  }), [captureIdentityGeneration, clearFinishedCall, ensureCallId, finishCall, isIdentityGenerationCurrent, recoverActiveCall]);

  useEffect(() => sipManager.on('sessionFailed', event => {
    const generation = captureIdentityGeneration();
    if (!isIdentityGenerationCurrent(generation)) return;
    void (async () => {
      if (intentionalEndRef.current || pageExitingRef.current) return;

      const transfer = transferInProgressRef.current;
      if (transfer) {
        transferInProgressRef.current = null;
        await clearFinishedCall(`Звонок переведён на добавочный ${transfer.destination}`);
        if (!isIdentityGenerationCurrent(generation)) return;
        return;
      }

      if (event.recovery) {
        setStatusMessage('Не удалось автоматически подключить разговор. Телефон повторит попытку.');
        window.setTimeout(() => {
          if (isIdentityGenerationCurrent(generation)) void recoverActiveCall();
        }, 1_500);
        return;
      }

      if (event.answeredElsewhere) {
        await clearFinishedCall('Звонок принят другим оператором');
        if (!isIdentityGenerationCurrent(generation)) return;
        return;
      }

      if (answeredRef.current) {
        const id = await ensureCallId(generation);
        if (!isIdentityGenerationCurrent(generation)) return;
        if (id) {
          try {
            const detached = await telephonyApi.detached(id);
            if (!isIdentityGenerationCurrent(generation)) return;
            if (detached.recoverable) {
              const detachedCall = detached.call;
              if (detachedCall) {
                setCall(current => ({ ...(current || detachedCall), ...detachedCall }));
                restoreServerCallClock(detachedCall);
              }
              setStatusMessage('Соединение с браузером прервано. Восстанавливаем разговор…');
              window.setTimeout(() => {
                if (isIdentityGenerationCurrent(generation)) void recoverActiveCall();
              }, 500);
              return;
            }
          } catch {
            if (!isIdentityGenerationCurrent(generation)) return;
            // Ниже зафиксируем звонок как завершённый/ошибочный.
          }
        }
      }

      const disposition = event.direction === 'INBOUND' && answeredRef.current
        ? 'FAILED'
        : dispositionForFailed(event);
      const cause = /rejected/i.test(event.cause || '')
        ? 'Абонент отклонил вызов'
        : event.cause || 'Звонок не состоялся';

      const finished = await finishCall(disposition, cause);
      if (!isIdentityGenerationCurrent(generation)) return;
      const parallelLeg = finished?.leg_disposition_ignored;
      await clearFinishedCall(
        parallelLeg
          ? finished.leg_disposition_reason === 'ANSWERED_ELSEWHERE'
            ? 'Звонок принят другим оператором'
            : 'Вызов продолжается у другого оператора'
          : cause,
      );
    })();
  }), [captureIdentityGeneration, clearFinishedCall, ensureCallId, finishCall, isIdentityGenerationCurrent, recoverActiveCall]);

  useEffect(() => {
    const refresh = () => { void configQuery.refetch(); };
    window.addEventListener('intez:phone-config-changed', refresh);
    return () => window.removeEventListener('intez:phone-config-changed', refresh);
  }, [configQuery.refetch]);

  useEffect(() => {
    setOperatorStatusState(config?.account?.operatorStatus || 'OFFLINE');
  }, [config?.account?.id, config?.account?.operatorStatus]);

  const resetIdentityState = useCallback(() => {
    qualityReporterRef.current?.reset();
    setCall(null);
    setSnapshot(INITIAL_SNAPSHOT);
    setOperatorStatusState('OFFLINE');
    setActionPending(false);
    setStatusMessage('');
    activeCallIdRef.current = null;
    sessionRegistrationRef.current = null;
    finalizedRef.current.clear();
    answeredRef.current = false;
    pageExitingRef.current = false;
    intentionalEndRef.current = false;
    transferInProgressRef.current = null;
    recoveryInFlightRef.current = false;
    lastRecoveryAttemptRef.current = 0;
    sipManager.restoreCallStartedAt(null);
  }, []);

  useEffect(() => {
    identityGenerationRef.current!.invalidate();
    const generation = captureIdentityGeneration();
    if (!enabled || !config) {
      void sipManager.stop();
      resetIdentityState();
      return () => { identityGenerationRef.current?.invalidate(); };
    }

    void sipManager.configure(config).catch(error => {
      if (!isIdentityGenerationCurrent(generation)) return;
      setStatusMessage(error instanceof Error ? error.message : 'Не удалось подключить SIP-телефон');
    });
    return () => { identityGenerationRef.current?.invalidate(); };
  }, [captureIdentityGeneration, enabled, isIdentityGenerationCurrent, resetIdentityState, sipConnectionKey, user?.id]);

  useEffect(() => () => {
    identityGenerationRef.current?.invalidate();
    qualityReporterRef.current?.reset();
    activeCallIdRef.current = null;
    sessionRegistrationRef.current = null;
    finalizedRef.current.clear();
    answeredRef.current = false;
    pageExitingRef.current = false;
    intentionalEndRef.current = false;
    transferInProgressRef.current = null;
    recoveryInFlightRef.current = false;
    lastRecoveryAttemptRef.current = 0;
    void sipManager.stop();
  }, []);

  useEffect(() => {
    if (!enabled) return;
    const generation = captureIdentityGeneration();
    if (!isIdentityGenerationCurrent(generation)) return;
    const source = new EventSource('/api/v1/telephony/events', { withCredentials: true });
    const refreshConfig = () => {
      if (isIdentityGenerationCurrent(generation)) void configQuery.refetch();
    };
    const handler = (raw: MessageEvent) => {
      if (!isIdentityGenerationCurrent(generation)) return;
      try {
        const payload = JSON.parse(raw.data || '{}') as TelephonyEvent;
        const incoming = payload.call;
        if (!incoming?.id) return;
        if (activeCallIdRef.current && incoming.id !== activeCallIdRef.current) return;
        if (!activeCallIdRef.current && [
          'telephony.call.created',
          'telephony.call.queued',
          'telephony.call.ringing',
        ].includes(payload.eventType)) {
          activeCallIdRef.current = incoming.id;
          finalizedRef.current.delete(incoming.id);
        }
        if (activeCallIdRef.current === incoming.id) {
          setCall(current => ({ ...(current || incoming), ...incoming }));
        }
        if (payload.eventType === 'telephony.call.answered') void updateOperatorStatus('CALL').catch(() => undefined);
        if (['telephony.call.completed', 'telephony.call.missed', 'telephony.call.failed'].includes(payload.eventType)) {
          void queryClient.invalidateQueries({ queryKey: ['telephony-calls'] });
        }
      } catch {
        // Heartbeats are valid SSE messages and require no UI action.
      }
    };
    const eventTypes = [
      'telephony.call.created',
      'telephony.call.queued',
      'telephony.call.ringing',
      'telephony.call.answered',
      'telephony.call.hold',
      'telephony.call.transferred',
      'telephony.call.completed',
      'telephony.call.missed',
      'telephony.call.failed',
    ];
    eventTypes.forEach(type => source.addEventListener(type, handler as EventListener));
    source.addEventListener('telephony.connected', refreshConfig);
    source.addEventListener('telephony.heartbeat', refreshConfig);
    source.onerror = () => {
      if (isIdentityGenerationCurrent(generation)) {
        setStatusMessage(current => current || 'Канал событий телефонии переподключается');
      }
    };
    return () => {
      source.removeEventListener('telephony.connected', refreshConfig);
      source.removeEventListener('telephony.heartbeat', refreshConfig);
      source.close();
    };
  }, [
    captureIdentityGeneration,
    configQuery.refetch,
    enabled,
    isIdentityGenerationCurrent,
    queryClient,
    sipConnectionKey,
    updateOperatorStatus,
  ]);

  const dial = useCallback(async (payload: OutboundCallPayload) => {
    const generation = captureIdentityGeneration();
    if (!isIdentityGenerationCurrent(generation)) return;
    if (!config?.account?.allowOutbound) throw new Error('Исходящие звонки запрещены');
    setActionPending(true);
    setStatusMessage('');
    try {
      await sipManager.requestMicrophone();
      if (!isIdentityGenerationCurrent(generation)) return;
      const created = await telephonyApi.outbound(payload);
      if (!isIdentityGenerationCurrent(generation)) return;
      activeCallIdRef.current = created.id;
      finalizedRef.current.delete(created.id);
      setCall({ ...created, contact_name: created.contact_name || payload.title || '' });
      const extraHeaders = [`X-Intez-Call-Id: ${created.id}`];
      if (created.external_uuid) {
        extraHeaders.push(`X-Intez-External-Uuid: ${created.external_uuid}`);
      }
      await sipManager.dial(payload.phone, { extraHeaders });
      if (!isIdentityGenerationCurrent(generation)) return;
    } catch (error) {
      if (!isIdentityGenerationCurrent(generation)) return;
      sipManager.cancelPreparedMicrophone();
      const message = error instanceof Error ? error.message : 'Не удалось начать звонок';
      setStatusMessage(message);
      await finishCall('FAILED', message);
      if (!isIdentityGenerationCurrent(generation)) return;
      throw error;
    } finally {
      if (isIdentityGenerationCurrent(generation)) setActionPending(false);
    }
  }, [captureIdentityGeneration, config?.account?.allowOutbound, finishCall, isIdentityGenerationCurrent]);

  // BEGIN INTEZ CALL RECOVERY V15.5.1
  useEffect(() => {
    const markExiting = () => {
      if (pageExitingRef.current) return;
      pageExitingRef.current = true;
      const id = activeCallIdRef.current;
      if (!id || !answeredRef.current) return;
      void telephonyApi.detached(id, true).catch(() => undefined);
    };
    const restorePage = () => { pageExitingRef.current = false; };

    window.addEventListener('beforeunload', markExiting, true);
    window.addEventListener('pagehide', markExiting, true);
    window.addEventListener('pageshow', restorePage);
    return () => {
      window.removeEventListener('beforeunload', markExiting, true);
      window.removeEventListener('pagehide', markExiting, true);
      window.removeEventListener('pageshow', restorePage);
    };
  }, []);
  // END INTEZ CALL RECOVERY V15.5.1

  useEffect(() => {
    const listener = (event: Event) => {
      const payload = (event as CustomEvent<OutboundCallPayload>).detail;
      if (payload?.phone) void dial(payload).catch(() => undefined);
    };
    window.addEventListener('intez:phone-dial', listener);
    return () => window.removeEventListener('intez:phone-dial', listener);
  }, [dial]);

  const answer = useCallback(async () => {
    const generation = captureIdentityGeneration();
    if (!isIdentityGenerationCurrent(generation)) return;
    if (actionPending) return;

    setActionPending(true);
    setStatusMessage('Подключаем гарнитуру и принимаем звонок…');

    try {
      await sipManager.answer();
      if (!isIdentityGenerationCurrent(generation)) return;
      answeredRef.current = true;
      setStatusMessage('Звонок принят');
    } catch (error) {
      if (!isIdentityGenerationCurrent(generation)) return;
      const message = error instanceof Error
        ? error.message
        : 'Не удалось принять входящий звонок';

      setStatusMessage(message);
      throw error;
    } finally {
      if (isIdentityGenerationCurrent(generation)) setActionPending(false);
    }
  }, [actionPending, captureIdentityGeneration, isIdentityGenerationCurrent]);

  const decline = useCallback(async () => {
    const generation = captureIdentityGeneration();
    if (!isIdentityGenerationCurrent(generation)) return;
    setActionPending(true);
    intentionalEndRef.current = true;
    try {
      sipManager.decline();
      await finishCall(call?.direction === 'INBOUND' ? 'MISSED' : 'CANCELLED', 'Отклонено пользователем');
      if (!isIdentityGenerationCurrent(generation)) return;
      await clearFinishedCall('Звонок отклонён');
      if (!isIdentityGenerationCurrent(generation)) return;
    } finally {
      if (isIdentityGenerationCurrent(generation)) {
        window.setTimeout(() => {
          if (isIdentityGenerationCurrent(generation)) intentionalEndRef.current = false;
        }, 1_500);
        setActionPending(false);
      }
    }
  }, [call?.direction, captureIdentityGeneration, clearFinishedCall, finishCall, isIdentityGenerationCurrent]);

  const hangup = useCallback(async () => {
    const generation = captureIdentityGeneration();
    if (!isIdentityGenerationCurrent(generation)) return;
    setActionPending(true);
    intentionalEndRef.current = true;
    try {
      sipManager.hangup();
      await finishCall('COMPLETED', 'Завершено пользователем');
      if (!isIdentityGenerationCurrent(generation)) return;
      await clearFinishedCall('Звонок завершён');
      if (!isIdentityGenerationCurrent(generation)) return;
    } finally {
      if (isIdentityGenerationCurrent(generation)) {
        window.setTimeout(() => {
          if (isIdentityGenerationCurrent(generation)) intentionalEndRef.current = false;
        }, 1_500);
        setActionPending(false);
      }
    }
  }, [captureIdentityGeneration, clearFinishedCall, finishCall, isIdentityGenerationCurrent]);

  const toggleMute = useCallback(async () => {
    const generation = captureIdentityGeneration();
    if (!isIdentityGenerationCurrent(generation)) return;
    if (snapshot.muted) sipManager.unmute(); else sipManager.mute();
  }, [captureIdentityGeneration, isIdentityGenerationCurrent, snapshot.muted]);

  const toggleHold = useCallback(async () => {
    const generation = captureIdentityGeneration();
    const id = await ensureCallId(generation);
    if (!isIdentityGenerationCurrent(generation)) return;
    if (!id) throw new Error('Звонок ещё не зарегистрирован');
    setActionPending(true);
    try {
      if (snapshot.held) {
        sipManager.unhold();
        const updated = await telephonyApi.resume(id);
        if (!isIdentityGenerationCurrent(generation)) return;
        setCall(current => ({ ...(current || updated), ...updated }));
      } else {
        sipManager.hold();
        const updated = await telephonyApi.hold(id);
        if (!isIdentityGenerationCurrent(generation)) return;
        setCall(current => ({ ...(current || updated), ...updated }));
      }
    } finally {
      if (isIdentityGenerationCurrent(generation)) setActionPending(false);
    }
  }, [captureIdentityGeneration, ensureCallId, isIdentityGenerationCurrent, snapshot.held]);

  const transfer = useCallback(async (destination: string) => {
    const generation = captureIdentityGeneration();
    const id = await ensureCallId(generation);
    if (!isIdentityGenerationCurrent(generation)) return;
    if (!id) throw new Error('Звонок ещё не зарегистрирован');
    const normalizedDestination = normalizeTransferDestination(destination);
    setActionPending(true);
    transferInProgressRef.current = { callId: id, destination: normalizedDestination };
    try {
      await telephonyApi.transfer(id, normalizedDestination);
      if (!isIdentityGenerationCurrent(generation)) return;
      if (transferInProgressRef.current?.callId === id) {
        setStatusMessage(transferProgressMessage());
      }
    } catch (error) {
      if (!isIdentityGenerationCurrent(generation)) return;
      if (transferInProgressRef.current?.callId === id) transferInProgressRef.current = null;
      const message = error instanceof Error ? error.message : 'Не удалось перевести звонок';
      setStatusMessage(message);
      throw error;
    } finally {
      if (isIdentityGenerationCurrent(generation)) setActionPending(false);
    }
  }, [captureIdentityGeneration, ensureCallId, isIdentityGenerationCurrent]);


  const selectTicket = useCallback(async (ticketId: string) => {
    const generation = captureIdentityGeneration();
    const id = await ensureCallId(generation);
    if (!isIdentityGenerationCurrent(generation)) return;
    if (!id) throw new Error('Звонок ещё не зарегистрирован');
    const normalized = String(ticketId || '').trim();
    if (!normalized) throw new Error('Выберите заявку');
    setStatusMessage('');
    try {
      const updated = await telephonyApi.selectBrowserTicket(id, normalized);
      if (!isIdentityGenerationCurrent(generation)) return;
      setCall(current => ({ ...(current || updated), ...updated }));
      setStatusMessage(updated.ticket_selection_message || 'Заявка связана со звонком');
      await queryClient.invalidateQueries({ queryKey: ['telephony-calls'] });
      if (!isIdentityGenerationCurrent(generation)) return;
    } catch (error) {
      if (!isIdentityGenerationCurrent(generation)) return;
      const message = error instanceof Error ? error.message : 'Не удалось связать звонок с заявкой';
      setStatusMessage(message);
      throw error;
    }
  }, [captureIdentityGeneration, ensureCallId, isIdentityGenerationCurrent, queryClient]);

  const saveResult = useCallback(async (payload: CallResultPayload) => {
    const generation = captureIdentityGeneration();
    const id = await ensureCallId(generation);
    if (!isIdentityGenerationCurrent(generation)) return;
    if (!id) {
      setCall(null);
      return;
    }
    setActionPending(true);
    try {
      await telephonyApi.saveResult(id, payload);
      if (!isIdentityGenerationCurrent(generation)) return;
      setCall(null);
      activeCallIdRef.current = null;
      sessionRegistrationRef.current = null;
      finalizedRef.current.delete(id);
      answeredRef.current = false;
      await updateOperatorStatus(snapshot.registered ? 'AVAILABLE' : 'OFFLINE').catch(() => undefined);
      if (!isIdentityGenerationCurrent(generation)) return;
      await queryClient.invalidateQueries({ queryKey: ['telephony-calls'] });
      if (!isIdentityGenerationCurrent(generation)) return;
      sipManager.completePostProcessing();
    } finally {
      if (isIdentityGenerationCurrent(generation)) setActionPending(false);
    }
  }, [captureIdentityGeneration, ensureCallId, isIdentityGenerationCurrent, queryClient, snapshot.registered, updateOperatorStatus]);

  const dismissPostProcessing = useCallback(() => {
    const generation = captureIdentityGeneration();
    if (!isIdentityGenerationCurrent(generation)) return;
    setCall(null);
    const previous = activeCallIdRef.current;
    activeCallIdRef.current = null;
    sessionRegistrationRef.current = null;
    if (previous) finalizedRef.current.delete(previous);
    sipManager.completePostProcessing();
    void updateOperatorStatus(snapshot.registered ? 'AVAILABLE' : 'OFFLINE').catch(() => undefined);
  }, [captureIdentityGeneration, isIdentityGenerationCurrent, snapshot.registered, updateOperatorStatus]);

  const value = useMemo<SipPhoneContextValue>(() => ({
    config,
    configLoading: configQuery.isLoading,
    snapshot,
    call,
    operatorStatus,
    actionPending,
    statusMessage,
    refreshConfig: configQuery.refetch,
    requestMicrophone,
    activateThisTab,
    dial,
    answer,
    decline,
    hangup,
    toggleMute,
    toggleHold,
    sendDtmf: tone => sipManager.sendDtmf(tone),
    transfer,
    selectTicket,
    saveResult,
    setOperatorStatus: updateOperatorStatus,
    dismissPostProcessing,
  }), [
    actionPending,
    activateThisTab,
    answer,
    call,
    config,
    configQuery.isLoading,
    configQuery.refetch,
    decline,
    dial,
    dismissPostProcessing,
    hangup,
    selectTicket,
    operatorStatus,
    requestMicrophone,
    saveResult,
    snapshot,
    statusMessage,
    toggleHold,
    toggleMute,
    transfer,
    updateOperatorStatus,
  ]);

  return <SipPhoneContext.Provider value={value}>{children}</SipPhoneContext.Provider>;
}

export function useSipPhoneContext(): SipPhoneContextValue {
  const value = useContext(SipPhoneContext);
  if (!value) throw new Error('useSipPhone должен использоваться внутри SipPhoneProvider');
  return value;
}
